import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PublicLayout } from '../components/layout/PublicLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  CreditCard, Smartphone, Check, Loader2, AlertCircle,
  Shield, Banknote, CheckCircle2, Clock, Wifi
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/* ─── Paynow logo as inline SVG text ─── */
const PaynowLogo = () => (
  <span
    style={{
      fontWeight: 900,
      fontSize: '1rem',
      background: 'linear-gradient(90deg,#e63946,#f4a261)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      letterSpacing: '-0.5px',
    }}
  >
    PAY<span style={{ WebkitTextFillColor: '#e63946' }}>now</span>
  </span>
);

const POLL_INTERVAL_MS = 5000;   // 5 seconds
const POLL_MAX_ATTEMPTS = 24;    // 2 minutes total

const PaymentPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('ecocash');
  const [phoneNumber, setPhoneNumber] = useState('');

  // EcoCash polling state
  const [paymentDoc, setPaymentDoc] = useState(null);
  const [pollingState, setPollingState] = useState(null); // 'waiting' | 'success' | 'timeout'
  const [pollAttempts, setPollAttempts] = useState(0);
  const [ecocashInstructions, setEcocashInstructions] = useState('');
  const pollTimerRef = useRef(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchBooking();
    return () => clearInterval(pollTimerRef.current);
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API_URL}/bookings/${bookingId}`);
      setBooking(response.data);
      setPaymentMethod(response.data.is_online_booking === false ? 'cash' : 'ecocash');
    } catch (error) {
      console.error('Failed to fetch booking:', error);
      toast.error('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  /* ─── Poll Paynow for EcoCash confirmation ─── */
  const startPolling = (pDocId) => {
    setPollingState('waiting');
    let attempts = 0;

    pollTimerRef.current = setInterval(async () => {
      attempts += 1;
      setPollAttempts(attempts);

      try {
        const res = await axios.get(`${API_URL}/payments/poll/${pDocId}`);
        if (res.data.paid) {
          clearInterval(pollTimerRef.current);
          setPollingState('success');
          toast.success('Payment confirmed! 🎉');
          setTimeout(() => navigate(`/ticket/${booking.booking_ref}`), 1800);
        } else if (attempts >= POLL_MAX_ATTEMPTS) {
          clearInterval(pollTimerRef.current);
          setPollingState('timeout');
        }
      } catch {
        // network blip — keep trying
      }
    }, POLL_INTERVAL_MS);
  };

  const currencySymbol = booking?.currency === 'USD' ? '$' : 'R';

  // Pay forward balance (only if is_pay_forward is true)
  const payForwardBalance = (booking?.is_pay_forward && booking?.pay_forward_balance > 0)
    ? booking.pay_forward_balance
    : 0;

  // Unpaid balance
  const totalUnpaid = booking ? Math.max(0, booking.total_amount - (booking.paid_amount || 0)) : 0;

  // - Cash: 0 to pay online now, full unpaid balance to pay at station
  // - EcoCash / Card / Online: actual online amount to pay (totalUnpaid - payForwardBalance), remaining payForwardBalance at station
  const amountToPayNow = paymentMethod === 'cash'
    ? 0
    : Math.max(0, totalUnpaid - payForwardBalance);

  const balanceAtStation = paymentMethod === 'cash'
    ? totalUnpaid
    : payForwardBalance;
  const amountDisplayed = paymentMethod === 'cash' ? totalUnpaid : amountToPayNow;
  const amountLabel = paymentMethod === 'cash' ? 'Amount Due at Station' : 'Amount to Pay';

  const handlePayment = async () => {
    if (paymentMethod === 'ecocash' && !phoneNumber) {
      toast.error('Please enter your EcoCash phone number');
      return;
    }

    const ticketUrl = `/ticket/${booking.booking_ref}`;

    /* Cash: open the ticket. Online bookings remain pending until staff approve payment. */
    if (paymentMethod === 'cash') {
      if (booking.is_online_booking === false) {
        toast.info('Ticket generated. Choose a print option.');
      } else {
        toast.info('Your seat is reserved. Pay at the station; staff must approve payment before boarding.');
      }
      navigate(ticketUrl);
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/payments`, {
        booking_id: booking.id,
        ...(paymentMethod !== 'paystack' && { amount: amountToPayNow, currency: booking.currency }),
        payment_method: paymentMethod,
        phone_number: paymentMethod === 'ecocash' ? phoneNumber : null,
      });

      const pDoc = response.data;
      setPaymentDoc(pDoc);

      if (paymentMethod === 'ecocash') {
        /* Mobile checkout — show instructions and start polling */
        if (pDoc.instructions) {
          setEcocashInstructions(pDoc.instructions);
        }
        toast.info('Check your phone for the EcoCash payment prompt!');
        startPolling(pDoc.id);

      } else if (paymentMethod === 'card' || paymentMethod === 'paystack') {
        /* Web checkout — redirect to the hosted gateway page */
        if (pDoc.redirect_url) {
          const gatewayName = paymentMethod === 'paystack' ? 'Paystack' : 'Paynow';
          toast.info(`Redirecting to ${gatewayName} secure payment page…`);
          window.location.href = pDoc.redirect_url;
        } else {
          toast.error('Could not get payment redirect URL. Please try again.');
        }
      }
    } catch (error) {
      console.error('Payment failed:', error);
      toast.error(error.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  /* ─── Loading skeleton ─── */
  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-40 bg-slate-200 rounded-lg" />
            <div className="h-64 bg-slate-200 rounded-lg" />
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (!booking) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 py-8 text-center">
          <AlertCircle className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold mb-2">Booking Not Found</h2>
          <Button onClick={() => navigate('/')} className="btn-panashe">
            Search Again
          </Button>
        </div>
      </PublicLayout>
    );
  }

  /* ─── EcoCash polling UI ─── */
  if (pollingState === 'success') {
    return (
      <PublicLayout>
        <div className="max-w-lg mx-auto px-4 py-12 text-center">
          <CheckCircle2 className="h-20 w-20 text-green-500 mx-auto mb-6 animate-bounce" />
          <h2 className="font-heading text-2xl font-bold mb-2">Payment Confirmed</h2>
          <p className="text-slate-500 mb-6">Redirecting you to your ticket…</p>
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
        </div>
      </PublicLayout>
    );
  }

  if (pollingState === 'waiting') {
    return (
      <PublicLayout>
        <div className="max-w-lg mx-auto px-4 py-12 text-center">
          <div className="relative mx-auto mb-6 w-24 h-24">
            <Wifi className="h-24 w-24 text-green-500 mx-auto animate-pulse" />
          </div>
          <h2 className="font-heading text-2xl font-bold mb-2">Awaiting EcoCash Payment</h2>
          <p className="text-slate-500 mb-4">
            A payment prompt has been sent to <strong>{phoneNumber}</strong>.<br />
            Please approve it on your phone to confirm your ticket.
          </p>

          {ecocashInstructions && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-sm text-green-800 text-left">
              <p className="font-semibold mb-1">📱 EcoCash Instructions:</p>
              <p style={{ whiteSpace: 'pre-line' }}>{ecocashInstructions}</p>
            </div>
          )}

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <Clock className="h-5 w-5 text-amber-500 flex-shrink-0" />
            <p className="text-sm text-slate-600">
              Checking payment status… ({pollAttempts * 5}s / 120s)
            </p>
          </div>

          <div className="w-full bg-slate-200 rounded-full h-2 mb-6">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${Math.min((pollAttempts / POLL_MAX_ATTEMPTS) * 100, 100)}%` }}
            />
          </div>

          <p className="text-xs text-slate-400">
            Do not close this page. We'll confirm automatically once payment is received.
          </p>
        </div>
      </PublicLayout>
    );
  }

  if (pollingState === 'timeout') {
    return (
      <PublicLayout>
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <AlertCircle className="h-16 w-16 text-amber-400 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold mb-2">Payment Not Confirmed Yet</h2>
          <p className="text-slate-500 mb-6">
            We could not confirm your EcoCash payment within 2 minutes.<br />
            If you approved the prompt, your ticket will be confirmed shortly.
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="outline"
              onClick={() => navigate(`/ticket/${booking.booking_ref}`)}
            >
              View My Ticket
            </Button>
            <Button className="btn-panashe" onClick={() => {
              setPollingState(null);
              setPaymentDoc(null);
              setPollAttempts(0);
              setProcessing(false);
            }}>
              Try Again
            </Button>
          </div>
        </div>
      </PublicLayout>
    );
  }

  /* ─── Main payment selection UI ─── */
  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="font-heading text-3xl font-bold text-center text-slate-900 mb-2">
          Complete Payment
        </h1>
        <p className="text-center text-slate-600 mb-8">
          Booking Reference: <span className="font-mono font-bold text-panashe-blue">{booking.booking_ref}</span>
        </p>

        {/* Amount Card */}
        <Card className="card-panashe mb-6 overflow-hidden">
          <div className="bg-panashe-blue p-6 text-center text-white">
            <p className="text-sm text-white/70 mb-1">{amountLabel}</p>
            <p className="font-heading text-4xl font-bold">
              {currencySymbol}{amountDisplayed.toFixed(2)}
            </p>
            {balanceAtStation > 0 && paymentMethod !== 'cash' && (
              <p className="text-sm text-white/70 mt-2">
                Balance to pay at station: {currencySymbol}{balanceAtStation.toFixed(2)}
              </p>
            )}
          </div>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Route</p>
                <p className="font-medium">{booking.trip_details?.origin} → {booking.trip_details?.destination}</p>
              </div>
              <div>
                <p className="text-slate-500">Seats</p>
                <p className="font-medium">{booking.seat_numbers.join(', ')}</p>
              </div>
              <div>
                <p className="text-slate-500">Passengers</p>
                <p className="font-medium">{booking.passengers.length}</p>
              </div>
              <div>
                <p className="text-slate-500">Currency</p>
                <p className="font-medium">{booking.currency}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Methods */}
        <Card className="card-panashe mb-6">
          <CardHeader>
            <CardTitle className="font-heading">Select Payment Method</CardTitle>
          </CardHeader>
          <CardContent>
            <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">

              <div
                className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${
                  paymentMethod === 'cash' ? 'border-panashe-blue bg-panashe-blue/5' : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => setPaymentMethod('cash')}
                data-testid="payment-cash"
              >
                <RadioGroupItem value="cash" id="cash" />
                <div className="flex-1">
                  <Label htmlFor="cash" className="cursor-pointer font-medium flex items-center gap-2">
                    <Banknote className="h-5 w-5 text-emerald-600" />
                    {booking.is_online_booking === false ? 'Cash Payment' : 'Pay at Station'}
                  </Label>
                  <p className="text-sm text-slate-500">
                    {booking.is_online_booking === false
                      ? 'Pay USD or ZAR cash at the station before boarding'
                      : 'Reserve your seat now, then pay in cash at the station before boarding'}
                  </p>
                </div>
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">At Station</span>
              </div>

              {/* EcoCash via Paynow */}
              <div
                className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${
                  paymentMethod === 'ecocash' ? 'border-panashe-blue bg-panashe-blue/5' : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => setPaymentMethod('ecocash')}
                data-testid="payment-ecocash"
              >
                <RadioGroupItem value="ecocash" id="ecocash" />
                <div className="flex-1">
                  <Label htmlFor="ecocash" className="cursor-pointer font-medium flex items-center gap-2">
                    <Smartphone className="h-5 w-5 text-green-600" />
                    EcoCash
                    <span className="ml-1 text-xs text-slate-400 font-normal">via</span>
                    <PaynowLogo />
                  </Label>
                  <p className="text-sm text-slate-500">Get a USSD prompt on your EcoCash number</p>
                </div>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Instant</span>
              </div>

              {/* Card via Paynow */}
              <div
                className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${
                  paymentMethod === 'card' ? 'border-panashe-blue bg-panashe-blue/5' : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => setPaymentMethod('card')}
                data-testid="payment-card"
              >
                <RadioGroupItem value="card" id="card" />
                <div className="flex-1">
                  <Label htmlFor="card" className="cursor-pointer font-medium flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-blue-600" />
                    Card / Other
                    <span className="ml-1 text-xs text-slate-400 font-normal">via</span>
                    <PaynowLogo />
                  </Label>
                  <p className="text-sm text-slate-500">Visa, Mastercard &amp; more — secure Paynow checkout</p>
                </div>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Online</span>
              </div>

              {/* Paystack */}
              <div
                className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${
                  paymentMethod === 'paystack' ? 'border-panashe-blue bg-panashe-blue/5' : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => setPaymentMethod('paystack')}
                data-testid="payment-paystack"
              >
                <RadioGroupItem value="paystack" id="paystack" />
                <div className="flex-1">
                  <Label htmlFor="paystack" className="cursor-pointer font-medium flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-emerald-600" />
                    Paystack
                  </Label>
                  <p className="text-sm text-slate-500">Secure card and local payment checkout</p>
                </div>
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">Online</span>
              </div>

            </RadioGroup>

            {/* Cash explanation */}
            {paymentMethod === 'cash' && (
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 font-semibold flex items-center gap-2">
                  <Banknote className="h-4 w-4" />
                  Pay at the Station
                </p>
                <ol className="text-xs text-amber-700 mt-2 space-y-1 list-decimal list-inside">
                  <li>Reserve your seat now; your ticket will remain PENDING</li>
                  <li>Visit the station and pay the amount due in cash before departure</li>
                  <li>Station staff will approve your payment and confirm your ticket</li>
                  <li>Your ticket must be CONFIRMED before boarding the bus</li>
                </ol>
                <p className="text-xs text-amber-600 mt-2 font-semibold">
                  Unpaid reservations are released four hours before departure.
                </p>
              </div>
            )}

            {/* EcoCash phone input */}
            {paymentMethod === 'ecocash' && (
              <div className="mt-6 p-4 bg-slate-50 rounded-lg space-y-3">
                <Label htmlFor="ecocashPhone">EcoCash Phone Number</Label>
                <Input
                  id="ecocashPhone"
                  type="tel"
                  placeholder="077 123 4567 or +263 77 123 4567"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="mt-1"
                  data-testid="ecocash-phone-input"
                />
                <p className="text-xs text-slate-500">
                  You will receive a USSD prompt on this number to approve the payment.
                </p>
                <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
                  <Shield className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-green-700">
                    Processed securely via <strong>Paynow Zimbabwe</strong>. Your booking is confirmed instantly on approval.
                  </p>
                </div>
              </div>
            )}

            {/* Card note */}
            {(paymentMethod === 'card' || paymentMethod === 'paystack') && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-sm text-blue-800 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-blue-600" />
                  You will be redirected to the <strong>{paymentMethod === 'paystack' ? 'Paystack' : 'Paynow'}</strong> secure payment page.
                  After payment you'll be automatically returned here.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <AlertCircle className="h-4 w-4 inline mr-1" />
            <strong>Secure Payment:</strong> Your booking will be confirmed after payment.
            An SMS confirmation will be sent to your phone.
          </p>
        </div>

        {/* Pay Button */}
        <Button
          className="w-full btn-panashe text-lg h-14"
          onClick={handlePayment}
          disabled={processing}
          data-testid="confirm-payment-btn"
        >
          {processing ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Processing…
            </span>
          ) : paymentMethod === 'cash' ? (
            <span className="flex items-center gap-2">
              <Banknote className="h-5 w-5" />
              {booking.is_online_booking === false ? 'Get Ticket (Pay Cash at Station)' : 'Reserve Seat, Pay at Station'}
            </span>
          ) : paymentMethod === 'ecocash' ? (
            <span className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Send EcoCash Prompt — {currencySymbol}{amountToPayNow.toFixed(2)}
            </span>
          ) : paymentMethod === 'paystack' ? (
            <span className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Pay {currencySymbol}{amountToPayNow.toFixed(2)} via Paystack
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Pay {currencySymbol}{amountToPayNow.toFixed(2)} via Paynow
            </span>
          )}
        </Button>

        <p className="text-center text-xs text-slate-500 mt-4">
          By completing this payment, you agree to our terms of service and cancellation policy.
        </p>
      </div>
    </PublicLayout>
  );
};

export default PaymentPage;
